import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import { calculateReadTime } from '../utils/helpers';

const BlogList = ({ navigateToHome, navigateToBlog, navigateToB2B, allBlogPosts, onViewBlogPost, backendUrl, navigateToPrivacy, navigateToTerms, navigateToAbout, navigateToSitemap }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [blogSearchQuery, setBlogSearchQuery] = useState('');
  const [blogSearchResults, setBlogSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Handle blog search
  const handleBlogSearch = async (e) => {
    e.preventDefault();
    if (!blogSearchQuery.trim() || blogSearchQuery.trim().length < 2) {
      setSearchError('A keresési kifejezésnek legalább 2 karakter hosszúnak kell lennie.');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    
    try {
      const response = await fetch(`${backendUrl}/api/blog/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: blogSearchQuery.trim() })
      });
      
      if (response.ok) {
        const searchResult = await response.json();
        setBlogSearchResults(searchResult.posts);
        setSearchPerformed(true);
      } else if (response.status === 429) {
        setSearchError('Túl sok keresési kérés. Kérjük próbálja újra 1 perc múlva.');
      } else {
        const error = await response.json();
        setSearchError(error.detail || 'Hiba történt a keresés során');
      }
    } catch (error) {
      console.error('Blog search error:', error);
      setSearchError('Hálózati hiba történt');
    } finally {
      setIsSearching(false);
    }
  };

  // Clear search
  const clearBlogSearch = () => {
    setBlogSearchQuery('');
    setBlogSearchResults([]);
    setSearchPerformed(false);
    setSearchError('');
  };

  // SEO setup for Blog page
  useEffect(() => {
    // Update page title
    document.title = "Fogászat Blog | FogPortál.hu - | Fogászat és fogorvos tippek, tudásanyag";
    
    // Update meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.getElementsByTagName('head')[0].appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Blog a mosoly egészségéért: esztétikai fogászat, fogfehérítés, megelőzés és szájhigiéniai praktikák. Hasznos olvasmányok a FogPortál.hu oldalán.');
    
    // Add keywords meta tag
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.getElementsByTagName('head')[0].appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', 'fogászat blog, fogorvos tanácsok, szájhigiénia, esztétikai fogászat, fogfehérítés, fogászati tippek, fogápolás, fog egészség');
    
    // Cleanup function to reset meta tags when component unmounts
    return () => {
      document.title = "Találja meg a legjobb fogorvost – FogPortál.hu országos kereső";
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Fogászat kereső és fogorvos kereső országos lefedettséggel. Részletes adatok, szakterületek, nyitvatartás – minden, ami segít a választásban.');
      }
    };
  }, []);
  
  return (
  <div className="min-h-screen bg-gray-50">
    {/* Modern Header */}
    <header className="bg-white shadow-lg border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={navigateToHome}
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-green-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1C7.6 1 4 4.6 4 9c0 2.2.9 4.2 2.3 5.7C7.1 15.4 8 16.7 8 18c0 .6.4 1 1 1s1-.4 1-1c0-1.8-1.1-3.4-2.8-4.1C6 12.1 5.5 10.6 5.5 9c0-3.6 2.9-6.5 6.5-6.5s6.5 2.9 6.5 6.5c0 1.6-.5 3.1-1.7 4.9C15 15.6 14 17.2 14 18c0 .6.4 1 1 1s1-.4 1-1c0-1.3.9-2.6 1.7-3.3C19.1 13.2 20 11.2 20 9c0-4.4-3.6-8-8-8zm0 4c-2.2 0-4 1.8-4 4 0 1.1.4 2.1 1.2 2.8.5.4.8 1 .8 1.6 0 .6-.4 1-1 1s-1-.4-1-1c0-.4-.2-.7-.5-.9C6.2 11.7 6 10.9 6 10c0-3.3 2.7-6 6-6s6 2.7 6 6c0 .9-.2 1.7-1.5 2.5-.3.2-.5.5-.5.9 0 .6-.4 1-1 1s-1-.4-1-1c0-.6.3-1.2.8-1.6.8-.7 1.2-1.7 1.2-2.8 0-2.2-1.8-4-4-4z"/>
                </svg>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-green-600 bg-clip-text text-transparent">
                FogPortál.hu
              </h1>
            </button>
          </div>

          {/* Navigation with Blog Search */}
          <nav className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-1">
              <button 
                onClick={navigateToHome}
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium text-sm transition-all duration-200"
              >
                Főoldal
              </button>
              <button 
                onClick={navigateToBlog}
                className="px-4 py-2 text-blue-600 bg-blue-50 rounded-lg font-medium text-sm hover:bg-blue-100 transition-all duration-200 shadow-sm"
              >
                Blog
              </button>
              <button 
                onClick={navigateToB2B}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-green-600 text-white rounded-lg font-medium text-sm hover:from-blue-700 hover:to-green-700 transition-all duration-200 shadow-md hover:shadow-lg ml-2"
              >
                Megjelenés fogászatoknak
              </button>
            </div>
            
            {/* Blog Search */}
            <form onSubmit={handleBlogSearch} className="flex items-center gap-2">
              <div className="relative">
                <input
                  type="text"
                  value={blogSearchQuery}
                  onChange={(e) => setBlogSearchQuery(e.target.value)}
                  placeholder="Keresés a blogban..."
                  className="w-80 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {blogSearchQuery && (
                  <button
                    type="button"
                    onClick={clearBlogSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button
                type="submit"
                disabled={isSearching || !blogSearchQuery.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium text-sm"
              >
                {isSearching ? 'Keresés...' : 'Keresés'}
              </button>
            </form>
          </nav>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:text-blue-600 hover:bg-gray-50"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} 
                />
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <div className="flex flex-col space-y-2 pt-4 px-4">
              <button 
                onClick={() => {
                  navigateToHome();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all duration-200"
              >
                Főoldal
              </button>
              <button 
                onClick={() => {
                  navigateToBlog();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-blue-600 bg-blue-50 rounded-lg font-medium hover:bg-blue-100 transition-all duration-200 shadow-sm"
              >
                Blog
              </button>
              <button 
                onClick={() => {
                  navigateToB2B();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 text-left text-blue-600 bg-blue-50 rounded-lg font-medium hover:bg-blue-100 transition-all duration-200 shadow-sm"
              >
                Megjelenés fogászatoknak
              </button>
              <button 
                onClick={() => {
                  navigateToB2B();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-medium hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-md hover:shadow-lg text-left"
              >
                Kapcsolat
              </button>
            </div>
          </div>
        )}
      </div>
    </header>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <h2 className="text-5xl font-bold text-gray-900 mb-6">Fogászati Blog</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Hasznos információk, tanácsok és a legfrissebb hírek a fogászat világából. 
          Szakértőink írásai segítenek megérteni és megőrizni fogai egészségét.
        </p>
      </div>

      {/* Search Error Messages */}
      {searchError && (
        <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {searchError}
        </div>
      )}
      
      {/* Search Results Info */}
      {searchPerformed && (
        <div className="mb-8 flex items-center justify-between bg-blue-50 p-4 rounded-lg border border-blue-200">
          <p className="text-gray-700 font-medium">
            {blogSearchResults.length > 0 
              ? `${blogSearchResults.length} találat a "${blogSearchQuery}" keresésre`
              : `Nincs találat a "${blogSearchQuery}" keresésre`
            }
          </p>
          <button
            onClick={clearBlogSearch}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-1 rounded hover:bg-blue-100 transition-colors"
          >
            Keresés törlése
          </button>
        </div>
      )}

      {/* Featured Post or Search Results */}
      {!searchPerformed && allBlogPosts.length > 0 && (
        <div className="mb-16">
          <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-green-600/90 z-10"></div>
            <div className="relative z-20 p-12 text-white">
              <div className="max-w-4xl">
                <span className="inline-block bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
                  Kiemelt cikk
                </span>
                <h3 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                  {allBlogPosts[0].title}
                </h3>
                <p className="text-xl mb-6 text-blue-100 line-clamp-3">
                  {allBlogPosts[0].excerpt}
                </p>
                <div className="flex items-center space-x-6 mb-8">
                  <span className="text-blue-100">{allBlogPosts[0].author}</span>
                  <span className="text-blue-100">•</span>
                  <span className="text-blue-100">
                    {new Date(allBlogPosts[0].created_at).toLocaleDateString('hu-HU')}
                  </span>
                  <span className="text-blue-100">•</span>
                  <span className="text-blue-100">{calculateReadTime(allBlogPosts[0].content)} perc olvasás</span>
                </div>
                <button 
                  onClick={() => onViewBlogPost(allBlogPosts[0].id, allBlogPosts[0].slug)}
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg hover:bg-blue-50 transition-colors font-semibold text-lg"
                >
                  Olvass tovább →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blog Posts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {(searchPerformed ? blogSearchResults : allBlogPosts.slice(1)).map(post => (
          <article 
            key={post.id} 
            onClick={() => onViewBlogPost(post.id, post.slug)}
            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all cursor-pointer group hover:-translate-y-1 transform"
          >
            {post.featured_image ? (
              <div className="aspect-video overflow-hidden">
                <img 
                  src={`${backendUrl}${post.featured_image}`} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            ) : (
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-green-100 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-blue-700 transition-colors">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-600">Blogcikk</p>
                </div>
              </div>
            )}
            
            <div className="p-6">
              <div className="mb-3">
                <span className="inline-block bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                  {(post.categories && post.categories.length > 0) ? post.categories[0] : 'Fogászati Tanácsok'}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                {post.title}
              </h3>
              
              <p className="text-gray-600 line-clamp-3">
                {post.excerpt}
              </p>
            </div>
          </article>
        ))}
      </div>

      {/* Load More Button */}
      {!searchPerformed && allBlogPosts.length > 9 && (
        <div className="text-center mt-12">
          <button className="bg-gray-900 text-white px-8 py-3 rounded-lg hover:bg-gray-800 transition-colors font-semibold">
            További cikkek betöltése
          </button>
        </div>
      )}
    </div>
    
    {/* Footer */}
    <Footer 
      navigateToHome={navigateToHome}
      navigateToBlog={navigateToBlog}
      navigateToB2B={navigateToB2B}
      navigateToPrivacy={() => window.location.href = '/adatvedelem'}
      navigateToTerms={() => window.location.href = '/aszf'}
      navigateToAbout={() => window.location.href = '/rolunk'}
      navigateToSitemap={navigateToSitemap}
    />
  </div>
  );
};


export default BlogList;
